import { CSSProperties, useState, useEffect } from 'react';
import useCampaignBuilder from "Hooks/useCampaignBuilder";
import useFacebookConnections from "Hooks/useFacebookConnections";
import useScreen from "Hooks/useScreen";
import { PlaybookStepName, PlaybookType } from "modules/Const";
import { RecordDetails, RecordSelectorType, Track } from "modules/Types";
import { clsx } from "modules/Utils";
import RecordSearchView from "elements/RecordSearchView";
import InputSymphony, { SelectOptionItem } from 'elements/InputSymphony';
import { Body2 } from 'components/shareable/Typography';
import Colors from 'modules/Colors';
import Sty from 'modules/Sty';
import ButtonSymphony from 'elements/ButtonSymphony';
import Banner from 'elements/Banner';
import Icon from 'elements/Icon';
import InfoSection from 'components/InfoSection';

// Extended RecordDetails type to include the properties we need
interface ExtendedRecordDetails extends RecordDetails {
    mainTrack?: Track;
    tracks?: Track[];
    highlightedTrack?: {
        id: string;
        uri: string;
        name: string;
    };
}

interface RecordSelectorViewProps {
    className?: string;
    cssCalcHeight?: number;
    options?: RecordSelectorType[];
    handleSelectRecord?: (recordDetails: RecordDetails) => void;
}

/** Configuration interface for the reusable record selector function */
interface RecordSelectorOptionsConfig {
    /** Condition to check if this selector should render */
    shouldRender: boolean;
    /** Title text for the banner section */
    title: string;
    /** Description text for the banner section */
    description: string;
    /** Input component to render in the banner */
    inputComponent: JSX.Element;
    /** Function to call when back button is clicked */
    onBack: () => void;
    /** Function to call when continue button is clicked */
    onContinue: () => void;
    /** Text for the continue button */
    continueButtonText: string;
    /** Text for the back button */
    backButtonText: string;
}

const RecordSelectorView = ({
    className,
    cssCalcHeight,
    options = ['single', 'project', 'playlist'],
    handleSelectRecord: handleSelectRecordProp
}: RecordSelectorViewProps) => {
    const { cssCalc } = useScreen();
    const {
        playbookType,
        nextCampaignStep,
        setCurrentStep,
        setSelectedSongToPromote,
        setHighlightedTrack,
        pitchNotes,
        setPitchNotes
    } = useCampaignBuilder();

    // State for the selected record and highlighted track
    const [recordSelected, setRecordSelected] = useState<ExtendedRecordDetails | null>(null);
    const [showTrackSelector, setShowTrackSelector] = useState<boolean>(false);
    const [showPitchNotes, setShowPitchNotes] = useState<boolean>(false);
    const [trackSearchTerm, setTrackSearchTerm] = useState<string>('');
    const [trackOptions, setTrackOptions] = useState<SelectOptionItem[]>([]);

    // Reset states when component unmounts
    useEffect(() => {
        return () => {
            setRecordSelected(null);
            setShowTrackSelector(false);
            setShowPitchNotes(false);
            setTrackSearchTerm('');
            setTrackOptions([]);
        };
    }, []);

    // Update track options when record is selected
    useEffect(() => {
        if (recordSelected && recordSelected.tracks && recordSelected.tracks.length > 0) {
            const options = recordSelected.tracks.map((track: Track) => ({
                value: track.spotify_id || track.id || '',
                title: track.name,
                description: track.explicit ? 'Explicit' : undefined,
                explicit: track.explicit,
                artist: track.artist_names || '',
                object: track
            }));
            setTrackOptions(options);

            // Auto-select the first track
            if (options.length > 0) {
                const firstTrack = options[0];
                if (firstTrack.object) {
                    // Update the selected record with the track
                    setRecordSelected(prevState => ({
                        ...prevState!,
                        mainTrack: firstTrack.object
                    }));

                    // Update the search term to show the selected track name
                    setTrackSearchTerm(firstTrack.title || '');
                }
            }
        }
    }, [recordSelected?.id]); // Only run when the record ID changes, not on every record change

    const handleSelectRecord = async (recordDetails: RecordDetails) => {
        const extendedRecord = recordDetails as ExtendedRecordDetails;
        setRecordSelected(extendedRecord);

        // Check if this is a playlist for relevant playbook types
        const shouldShowTrackSelector =
            recordDetails.type === 'playlist' &&
            (playbookType === PlaybookType.GROW_PLAYLIST_FOLLOWERS ||
                playbookType === PlaybookType.INCREASE_MUSIC_STREAMS);

        // Check if this is a playlisting campaign
        const shouldShowPitchNotes = playbookType === PlaybookType.GET_PLAYLISTED;

        if (shouldShowTrackSelector) {
            setShowTrackSelector(true);
        } else if (shouldShowPitchNotes) {
            // Show pitch notes for playlisting campaigns
            setSelectedSongToPromote(recordDetails);
            setShowPitchNotes(true);
        } else {
            // Regular flow - just set the song and continue
            setSelectedSongToPromote(recordDetails);

            handleSelectRecordProp?.(recordDetails);

            nextCampaignStep()
        }
    }

    const handleTrackSelect = (option: SelectOptionItem) => {
        if (recordSelected && option.object) {
            setRecordSelected({
                ...recordSelected,
                mainTrack: option.object
            });

            setTrackSearchTerm(option.title || '');
        }
    };

    const handleContinueWithTrack = () => {
        if (recordSelected && recordSelected.mainTrack) {
            const highlightedTrack = {
                id: recordSelected.mainTrack.id || recordSelected.mainTrack.spotify_id || '',
                uri: recordSelected.mainTrack.uri || recordSelected.mainTrack.spotify_uri || '',
                name: recordSelected.mainTrack.name || ''
            };

            setHighlightedTrack(highlightedTrack);

            const recordWithTrack = {
                ...recordSelected,
                highlightedTrack
            };

            setSelectedSongToPromote(recordWithTrack);

            if (playbookType === PlaybookType.INCREASE_MUSIC_STREAMS ||
                playbookType === PlaybookType.GROW_PLAYLIST_FOLLOWERS) {
                setCurrentStep(PlaybookStepName.CREATIVE);
            }
        }
    };

    const handlePitchNotesChange = (value: string) => {
        if (value.length <= 200 || value === '') {
            setPitchNotes(value);
        }
    };

    const handleContinueWithPitchNotes = () => {
        if (recordSelected) {
            handleSelectRecordProp?.(recordSelected);
            nextCampaignStep();
        }
    };



    /** Reusable function to render record selector with customizable content */
    const renderRecordSelector = (config: RecordSelectorOptionsConfig) => {
        if (!config.shouldRender || !recordSelected) {
            return null;
        }

        return (
            <div className="trackSelectorContainer" style={sty.trackSelectorContainer}>
                <div style={sty.recordInfoContainer}>
                    <div style={sty.recordImage}>
                        <img
                            src={recordSelected.thumbnail_url}
                            alt={recordSelected.name}
                            style={sty.recordThumbnail}
                        />
                    </div>
                    <div style={sty.recordInfo}>
                        <h3 style={Sty.text18bold}>{recordSelected.name}</h3>
                        <p style={Sty.text14grey}>{recordSelected.primary_artist}</p>
                    </div>
                </div>

                <Banner
                    color="maestro"
                    className="infoSection"
                    style={sty.infoSection}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Icon
                            className="maestroIcon"
                            icon="maestroBadgeImage"
                        />
                        <h3 style={Sty.text18bold}>{config.title}</h3>
                    </div>
                    <p style={Sty.text16grey}>{config.description}</p>

                    <div style={{ paddingTop: 16, paddingBottom: 16 }}>
                        {config.inputComponent}
                    </div>
                </Banner>

                <div style={sty.buttonContainer}>
                    <ButtonSymphony
                        className="backButton"
                        onClick={config.onBack}
                        variant="outlined"
                        color="grey"
                    >
                        {config.backButtonText}
                    </ButtonSymphony>
                    <ButtonSymphony
                        className="continueButton"
                        onClick={config.onContinue}
                        variant="outlined"
                        color="purple"
                    >
                        {config.continueButtonText}
                    </ButtonSymphony>
                </div>
            </div>
        );
    };

    const renderTrackAutoplaySelector = () => {
        const trackSelectorInput = (
            <InputSymphony
                className="trackSelector"
                label="Search for a track to autoplay"
                value={trackSearchTerm}
                onChange={setTrackSearchTerm}
                placeholder="Select a song"
                isSelectDropdown
                options={trackOptions}
                onOptionSelect={handleTrackSelect}
            />
        );

        return renderRecordSelector({
            shouldRender: (playbookType === PlaybookType.GROW_PLAYLIST_FOLLOWERS ||
                playbookType === PlaybookType.INCREASE_MUSIC_STREAMS) && !!recordSelected,
            title: 'Autoplay a Track',
            description: 'You can select a track from your playlist to autoplay when someone clicks on your ad.',
            inputComponent: trackSelectorInput,
            onBack: () => setShowTrackSelector(false),
            onContinue: handleContinueWithTrack,
            continueButtonText: !recordSelected?.mainTrack ? 'Skip' : 'Continue',
            backButtonText: 'Back to Playlist Selection'
        });
    };

    const renderPitchNotesSelector = () => {
        const pitchNotesInput = (
            <InputSymphony
                isTextArea
                rows={3}
                maxLength={200}
                className="trackSelector"
                value={pitchNotes}
                onChange={handlePitchNotesChange}
                placeholder="I'd like to focus on alternative rap playlists..."
            />
        );

        return renderRecordSelector({
            shouldRender: playbookType === PlaybookType.GET_PLAYLISTED && !!recordSelected,
            title: 'Pitch Notes (optional)',
            description: 'Enter any info you\'d like our pitching team to know as they reach out to curators. We recommend including song genres and details about related artists.\n\nNOTE: Our pitching team may broaden their search outside these guidelines if necessary (e.g. if there aren\'t as many well-engaged playlists within your defined niche), while still trying to stay true to the song. (optional)',
            inputComponent: pitchNotesInput,
            onBack: () => setShowPitchNotes(false),
            onContinue: handleContinueWithPitchNotes,
            continueButtonText: !recordSelected?.mainTrack ? 'Skip' : 'Continue',
            backButtonText: 'Back to Song Selection'
        });
    };

    const renderRecordSelectorTabInner = () => {
        if (showTrackSelector && recordSelected) {
            return renderTrackAutoplaySelector();
        }

        if (showPitchNotes && recordSelected) {
            return renderPitchNotesSelector();
        }

        return (
            <div className="recordSelectorTabInner middle-wrapper lg:px-0 relative">
                <RecordSearchView
                    className="recordSelectorTabRecordSearchView"
                    handleSelectRecord={handleSelectRecord}
                    options={options}
                    letUserSearchSpotify
                    letUserRefreshReleases
                    // maxHeight={cssCalc(72 + 72 + 64 + 276)}
                    maxHeight={cssCalc(cssCalcHeight)}
                />
            </div>
        );
    }

    return (
        <div
            className={clsx('recordSelectorViewComponent', className)}
            style={sty.recordSelectorViewComponent}
        >
            {renderRecordSelectorTabInner()}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    recordSelectorViewComponent: {
        width: '100%',
        backgroundColor: 'white',
    },
    trackSelectorContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        padding: 16,
    },
    recordInfoContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
    },
    recordImage: {
        width: 80,
        height: 80,
        borderRadius: 4,
        overflow: 'hidden',
    },
    recordThumbnail: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    recordInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    infoSection: {
        backgroundColor: Colors.greyLighter,
        padding: 16,
        borderRadius: 8,
        marginTop: 16,
        overflow: 'visible',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 24,
    },
    pitchNotesContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 16,
        backgroundColor: 'white',
        borderRadius: 8,
    },
    pitchNotesTextareaContainer: {
        position: 'relative',
        marginTop: 16,
    },
    characterCount: {
        position: 'absolute',
        bottom: 8,
        right: 8,
        fontSize: 12,
        color: Colors.grey,
        margin: 0,
    },
}

export default RecordSelectorView;